import React, { useContext, useState } from "react";
import styled, { ThemeContext } from "styled-components";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "@wit/mpesa-ui-components";
import { useSelector } from "react-redux";
import { IStoreInterface } from "../../../../configs/store.config";
import useVersionsDiff, { VersionItem } from "../../../../shared/hooks/use-version-diff/use-version-diff.hook";
import VersionItemStatusChip from "../../../../shared/hooks/use-version-diff/version-item-status-chip.component";
import { Row, Column } from "../../../../shared/shared.styled";

import { IDiscoverCard, DiscoverCardsStatusEnum } from "../discover-cards.model";
import moment from "moment";

import { getAvailableLanguagesOptions } from "../discover-cards.utils";
import LanguageSelectorDropdown from "../../../../shared/components/language-selector-dropdown.component";

interface IPublishDiscoverCardProps {
  onCancelClick: () => void;
  submit: () => void;
  isLoading: boolean;
}

/**
 * Publish Discover Card component
 */
const PublishDiscoverCard = ({ onCancelClick, isLoading, submit }: IPublishDiscoverCardProps) => {
  const [t] = useTranslation();
  const [language, setLanguage] = useState({ label: t("commons.languages.en"), key: "en" });

  const { discoverCards, lastVersion } = useSelector((state: IStoreInterface) => state.discoverCardsReducer);
  const { prev, current } = useVersionsDiff(lastVersion!.discoverCards, discoverCards!.discoverCards);
  const theme = useContext(ThemeContext);

  /**
   * Render item
   * @param item
   */
  const renderItem = (item: VersionItem<IDiscoverCard>) => {
    return (
      <ItemColumn disabled={false}>
        <Row>
          <ImageContainer>
            <Image background={item.urlImage} />
          </ImageContainer>
          <ItemInfo>
            <div style={{ display: "flex", flexDirection: "row" }}>
              {item.name && (
                <Row>
                  <span style={{ fontWeight: 1000 }}> {item.name[language.key]}</span>
                </Row>
              )}
              <VersionItemStatusChip status={item.versionItemStatus} />
            </div>
            {item.description && <Row style={{ marginTop: 7, marginBottom: 7 }}>{item.description[language.key]}</Row>}
            {item.endDate && item.endDate !== 0 ? (
              <DateInfo style={{}}>{`${t("pages.discoverCards.publishModal.expires")}: ${moment(
                item.endDate,
                "x",
              ).format("DD/MM/YYYY")}`}</DateInfo>
            ) : null}
          </ItemInfo>
          <StatusContainer>
            {item.status && (
              <StatusRow type={item.status}>
                {t(`commons.discoverCards.discoverCardsStatusEnum.${item.status}`)}
              </StatusRow>
            )}
          </StatusContainer>
        </Row>
      </ItemColumn>
    );
  };

  return (
    <ModalContainer>
      <Row style={{ width: "100%", padding: 24 }}>
        <Column style={{ width: "100%" }}>
          <ModalTitle>{t("pages.discoverCards.publishModal.title")}</ModalTitle>
          <Row>
            <ModalSubTitle>{t("pages.discoverCards.publishModal.wantPublish")}</ModalSubTitle>
            <RightContainer>
              <LanguageSelectorDropdown
                availableLanguages={getAvailableLanguagesOptions()}
                selectedLanguage={language}
                changeSelectedLanguage={setLanguage}
                color={theme.palette.turquoiseBlue}
                id={"publish-language-selector"}
              ></LanguageSelectorDropdown>
            </RightContainer>
          </Row>
        </Column>
      </Row>
      <Content>
        <Row>
          <ItemColumn>{t("pages.discoverCards.publishModal.previousVersion")}</ItemColumn>
          <ItemColumn>{t("pages.discoverCards.publishModal.editedVersion")}</ItemColumn>
        </Row>
        <ItemsContainer>
          {prev &&
            current &&
            current.map((edited, index) => {
              return (
                <Item key={edited.id}>
                  {prev[index] ? renderItem(prev[index]) : <ItemColumn />}
                  {edited ? renderItem(edited) : <ItemColumn />}
                </Item>
              );
            })}
        </ItemsContainer>
      </Content>
      <ButtonsContainer style={{ padding: 24 }}>
        <div>
          <PrimaryButton
            type="button"
            id="cancel-button"
            onClick={onCancelClick}
            titleLabel={t("pages.discoverCards.publishModal.cancelBtnLabel")}
          />
        </div>
        <div>
          <PrimaryButton
            id="confirm-button"
            redTheme={true}
            type="submit"
            disabled={isLoading}
            onClick={() => submit()}
            titleLabel={t("pages.discoverCards.publishModal.saveChangesBtnLabel")}
          />
        </div>
      </ButtonsContainer>
    </ModalContainer>
  );
};

export default PublishDiscoverCard;

const ModalContainer = styled("div")`
  display: flex;
  flex-direction: column;
  font-family: Vodafone Rg;
`;

const ModalTitle = styled("span")`
  font-family: Vodafone Rg;
  font-size: 22px;
  color: ${props => props.theme.palette.darkGrey};
  margin-bottom: 21px;
`;

const ModalSubTitle = styled(ModalTitle)`
  font-size: 18px;
  margin-bottom: 0;
`;

const ButtonsContainer = styled("div")`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 50px;

  > div {
    width: "fit-content";

    :first-of-type {
      margin-right: 12px;
    }
  }
`;

const ItemsContainer = styled.div`
  max-height: 220px;
  overflow: scroll;
  width: 100%;
  overflow-x: hidden;
`;

const Item = styled(Row)`
  display: flex;
  align-items: flex-start;
`;

const ImageContainer = styled(Column)`
  margin-right: 24px;
  display: flex;
  align-self: baseline;
  flex: 1;
  padding: 4px;
`;

const Image = styled("div")<{ background: any }>`
  width: 64px;
  height: 64px;
  background: url(${props => (props.background ? `${props.background}?time=${new Date().valueOf()}` : "none")});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 5px;
`;

const ItemColumn = styled(Column)<{ disabled?: Boolean }>`
  flex: 1;
  padding: 16px 0px 16px 0;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  padding-left: 24px;
  &:first-child {
    padding-right: 74px;
  }

  &:last-child {
    padding-left: 30px;
    padding-right: 44px;
  }
`;

const ItemInfo = styled(Column)`
  flex: 20;
`;
const StatusContainer = styled(Column)`
  padding-left: 24px;
  display: flex;
  align-self: end;
  flex: 1;
`;

const StatusRow = styled("div")<{ type: DiscoverCardsStatusEnum }>`
  ${props => {
    let color = props.theme.palette.digitalGreen;
    switch (props.type) {
      case DiscoverCardsStatusEnum.LIVE:
        color = props.theme.palette.digitalGreen;
        break;
      case DiscoverCardsStatusEnum.DISABLE:
        color = props.theme.palette.freshOrange;
        break;
      case DiscoverCardsStatusEnum.DISABLED:
        color = props.theme.palette.freshOrange;
        break;
      case DiscoverCardsStatusEnum.ENABLE:
      case DiscoverCardsStatusEnum.EDIT:
        color = props.theme.palette.digitalGreen;
        break;
      case DiscoverCardsStatusEnum.DELETE:
        color = props.theme.palette.vodafoneRed;
        break;
    }
    return `color: ${color};display: flex;justify-content: flex-end;`;
  }}
`;

const Content = styled.div`
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.25;
  letter-spacing: normal;
  color: ${props => props.theme.palette.darkGrey};

  background: ${props => `linear-gradient(90deg, ${props.theme.palette.lightGrey} 50%, transparent 50%)`};
`;

const DateInfo = styled(Row)`
  font-size: 14px;
  font-weight: normal;
  line-height: 1.29;
  opacity: 0.5;
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: 15px;
`;
